/** @file Converts bind-space weighted meshes to per-influence bone-local positions. */
import type { MeshAtt } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import { inverseBindPoses } from './inverseBindPoses';
import { requireCompiledIndex } from './requireCompiledIndex';

const IDENTITY = [1, 0, 0, 1, 0, 0];

/**
 * Returns the flattened skin array for a skinned mesh: per vertex `count, (bone, px, py, w) × count` with
 * `(px, py) = inverse(bindPose[bone]) · bindVertex` (§6.7), influences in document order. Rigid meshes give [].
 * Error paths are mesh-relative (the signature has no attachment id); compileAttachments' checkSkinnedMesh
 * reports the same defects at document paths before runCompile reaches this stage.
 */
export function compileSkinning(mesh: MeshAtt, boneIndex: Map<string, number>): number[] {
  const g = mesh.geometry;
  if (g.kind === 'rigid') return [];
  const inverse = inverseBindPoses(mesh, 'geometry');
  const out: number[] = [];
  const p: [number, number] = [0, 0];
  g.weights.forEach((influences, v) => {
    out.push(influences.length);
    for (const [bone, w] of influences) {
      affine.apply(inverse.get(bone) ?? IDENTITY, g.bindVertices[2 * v] ?? 0, g.bindVertices[2 * v + 1] ?? 0, p);
      out.push(requireCompiledIndex(boneIndex, bone, 'bone', `geometry.weights.${v}`), p[0], p[1], w);
    }
  });
  return out;
}
